import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { BarChart3, Users, Sparkles, Star, Clock, Activity, TrendingUp, Award, Eye, EyeOff, Save, FileText, MessageSquare } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmtHrs = (h) => `${h ?? 0}h`;
const fmtPct = (p) => `${(p ?? 0).toFixed?.(1) ?? p}%`;
const StarBar = ({ dist }) => {
  const total = Object.values(dist || {}).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="space-y-1" data-testid="star-distribution">
      {[5, 4, 3, 2, 1].map((s) => {
        const c = dist?.[s] ?? 0;
        const pct = (c * 100) / total;
        return (
          <div key={s} className="flex items-center gap-2 text-[11px]">
            <span className="w-6 text-right text-primary/60">{s}★</span>
            <div className="flex-1 h-3 bg-muted border border-border relative">
              <div className="h-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 text-right font-mono">{c}</span>
          </div>
        );
      })}
    </div>
  );
};

const Stat = ({ label, value, hint, tone = "default", icon: Icon }) => (
  <div className={`border p-4 ${tone === "accent" ? "border-accent/40 bg-accent/5" : "border-border"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="flex items-center gap-2 overline text-[10px] text-primary/50">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </div>
    <div className="font-display text-2xl mt-1 truncate">{value}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
  </div>
);

// Simple horizontal bar for the peak-hours 24-slot histogram
const Sparkline = ({ data }) => {
  const max = Math.max(1, ...data);
  return (
    <div className="flex items-end gap-0.5 h-16" data-testid="peak-hours-spark">
      {data.map((v, i) => {
        const h = Math.max(2, (v * 100) / max);
        const isPrime = v > 0 && v === max;
        return (
          // Hour-of-day is the semantic key here (data is always length 24).
          <div key={`hour-${i}`} className="flex-1 flex flex-col items-center gap-1">
            <div title={`${i.toString().padStart(2,'0')}:00 — ${v} bookings`}
                 className={`w-full ${isPrime ? "bg-accent" : "bg-primary/40"} hover:bg-accent transition-colors cursor-help`}
                 style={{ height: `${h}%` }} />
          </div>
        );
      })}
    </div>
  );
};

export default function StatisticsPanel() {
  const { admin } = useAuth();
  const isDev = admin?.role === "developer";
  const canEditVisibility = ["developer", "admin", "manager"].includes(admin?.role);
  const [stats, setStats] = useState(null);
  const [ratings, setRatings] = useState(null);        // aggregate
  const [ratingsDetailed, setRatingsDetailed] = useState(null); // dev only
  const [loading, setLoading] = useState(true);

  // Guest-menu visibility toggles (persisted in settings)
  const [vis, setVis] = useState({
    show_treatment_ratings: false,
    show_treatment_popularity: false,
    popularity_period: "30d",
    popularity_from: "",
    popularity_to: "",
  });
  const [visLoaded, setVisLoaded] = useState(false);
  const [visSaving, setVisSaving] = useState(false);
  // Charter report state
  const [charters, setCharters] = useState([]);
  const [selectedCharter, setSelectedCharter] = useState("");
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/admin/statistics"), api.get("/admin/ratings/aggregate")];
      if (isDev) reqs.push(api.get("/admin/ratings/detailed"));
      const [s, r, d] = await Promise.all(reqs);
      setStats(s.data); setRatings(r.data);
      if (d) setRatingsDetailed(d.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, [isDev]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Load current guest-menu visibility settings (public endpoint — anyone can read)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/settings");
        setVis({
          show_treatment_ratings: !!data.show_treatment_ratings,
          show_treatment_popularity: !!data.show_treatment_popularity,
          popularity_period: data.popularity_period || "30d",
          popularity_from: data.popularity_from || "",
          popularity_to: data.popularity_to || "",
        });
      } catch (e) { console.debug("[Statistics] public settings load failed:", e); }
      finally { setVisLoaded(true); }
    })();
  }, []);

  const saveVis = async (next) => {
    if (!canEditVisibility) return;
    setVisSaving(true);
    try {
      const payload = { ...vis, ...next };
      // Normalise custom-range strings (keep empty as null so backend stores clean)
      const body = {
        show_treatment_ratings: !!payload.show_treatment_ratings,
        show_treatment_popularity: !!payload.show_treatment_popularity,
        popularity_period: payload.popularity_period || "30d",
        popularity_from: payload.popularity_from || null,
        popularity_to: payload.popularity_to || null,
      };
      const { data } = await api.put("/admin/settings/menu-visibility", body);
      setVis({
        show_treatment_ratings: !!data.show_treatment_ratings,
        show_treatment_popularity: !!data.show_treatment_popularity,
        popularity_period: data.popularity_period || "30d",
        popularity_from: data.popularity_from || "",
        popularity_to: data.popularity_to || "",
      });
      toast.success("Guest menu visibility updated");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save");
    } finally { setVisSaving(false); }
  };

  // Charter report — Manager+ can pick any charter (active, upcoming, completed)
  useEffect(() => {
    if (!canEditVisibility) return;
    api.get("/admin/charters/list").then(({ data }) => {
      setCharters(data.items || []);
      // Auto-select the active charter if any, else the most recent
      const active = (data.items || []).find((c) => c.active);
      if (active) setSelectedCharter(active.id);
      else if ((data.items || []).length) setSelectedCharter(data.items[0].id);
    }).catch(() => {});
  }, [canEditVisibility]);

  useEffect(() => {
    if (!selectedCharter) { setReport(null); return; }
    setReportLoading(true);
    api.get(`/admin/statistics/charter/${selectedCharter}`)
      .then(({ data }) => setReport(data))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || "Report load failed"))
      .finally(() => setReportLoading(false));
  }, [selectedCharter]);

  const topTreatments = useMemo(() => stats?.treatments?.top || [], [stats]);
  const workload = useMemo(() => stats?.crew?.workload || [], [stats]);
  const peakHours = useMemo(() => stats?.peak_hours || Array(24).fill(0), [stats]);

  // Cross-reference treatment names into rating aggregates
  const treatRatingsList = useMemo(() => {
    if (!ratings || !topTreatments) return [];
    return topTreatments.map((t) => {
      const r = ratings.by_treatment?.[t.treatment_id] || null;
      return { ...t, rating: r };
    });
  }, [ratings, topTreatments]);

  if (loading && !stats) return <div className="text-sm text-muted-foreground p-6">Loading statistics…</div>;
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="statistics-panel">
      <div className="flex items-start gap-3 border border-accent/30 bg-accent/5 p-4">
        <BarChart3 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
        <div>
          <div className="overline text-accent">Statistics</div>
          <h2 className="font-display text-2xl mt-1">Spa operations dashboard</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Auto-refreshes every 30 s. Ratings are hidden until each charter completes unless the guest
            opts to publish immediately — {isDev ? "as Developer you can see guest names attached to ratings." : "guest names are anonymised on this view."}
          </p>
        </div>
      </div>

      {/* Guest-menu visibility controls — Manager+ can edit, others read-only */}
      {visLoaded && (
        <section className="border border-border bg-card" data-testid="menu-visibility-panel">
          <div className="p-4 border-b border-border flex items-start gap-2">
            <Eye className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="overline text-primary/50 text-[10px]">Guest menu · discreet badges</div>
              <h3 className="font-display text-lg mt-1">What the treatment cards reveal</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Turn on the badges to whisper a little social proof onto each treatment card —
                shown bottom-right in a small typographic mark, never a shouty stamp.
                {!canEditVisibility && <span className="ml-1 text-accent">Manager+ can change these; you always see them on your own view.</span>}
              </p>
            </div>
          </div>

          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ratings toggle */}
            <div className="flex items-start justify-between gap-3 border border-border p-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-accent" />
                  <span className="font-medium text-sm">Show guest ratings</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  Displays the average star score and the number of reviews.
                  Treatments that haven't been rated yet are shown as
                  <em className="text-foreground/70"> "Awaiting first ripple"</em>
                  &nbsp;— tasteful, never blank.
                </p>
              </div>
              <Switch
                checked={vis.show_treatment_ratings}
                onCheckedChange={(v) => saveVis({ show_treatment_ratings: v })}
                disabled={!canEditVisibility || visSaving}
                data-testid="toggle-show-ratings"
              />
            </div>

            {/* Popularity toggle */}
            <div className="flex items-start justify-between gap-3 border border-border p-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-accent" />
                  <span className="font-medium text-sm">Show popularity</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  Reveals how many times a treatment has been chosen in the selected period —
                  gives guests a sense of what's loved, even before the first review lands.
                </p>
              </div>
              <Switch
                checked={vis.show_treatment_popularity}
                onCheckedChange={(v) => saveVis({ show_treatment_popularity: v })}
                disabled={!canEditVisibility || visSaving}
                data-testid="toggle-show-popularity"
              />
            </div>
          </div>

          {/* Period selector — active whenever popularity is on OR user is editing */}
          <div className={`p-4 border-t border-border ${!vis.show_treatment_popularity ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-3.5 w-3.5 text-primary/60" />
              <span className="overline text-primary/60 text-[10px]">Popularity period</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="popularity-period-group">
              {[
                { k: "30d",     label: "Last 30 days" },
                { k: "charter", label: "Active charter" },
                { k: "year",    label: "This year" },
                { k: "custom",  label: "Custom range" },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  onClick={() => canEditVisibility && saveVis({ popularity_period: k })}
                  disabled={!canEditVisibility || visSaving}
                  data-testid={`period-${k}`}
                  className={`text-xs px-3 py-1.5 border rounded-full transition-colors ${
                    vis.popularity_period === k
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border hover:border-accent/50"
                  } ${!canEditVisibility ? "cursor-default" : "cursor-pointer"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {vis.popularity_period === "custom" && (
              <div className="flex flex-wrap items-end gap-3 mt-2" data-testid="popularity-custom-range">
                <div>
                  <label className="overline text-[10px] text-primary/50 block mb-1">From</label>
                  <Input
                    type="date"
                    value={vis.popularity_from ? vis.popularity_from.slice(0, 10) : ""}
                    onChange={(e) => setVis((p) => ({ ...p, popularity_from: e.target.value ? `${e.target.value}T00:00:00+00:00` : "" }))}
                    disabled={!canEditVisibility}
                    className="w-40 text-xs"
                    data-testid="popularity-from"
                  />
                </div>
                <div>
                  <label className="overline text-[10px] text-primary/50 block mb-1">To</label>
                  <Input
                    type="date"
                    value={vis.popularity_to ? vis.popularity_to.slice(0, 10) : ""}
                    onChange={(e) => setVis((p) => ({ ...p, popularity_to: e.target.value ? `${e.target.value}T23:59:59+00:00` : "" }))}
                    disabled={!canEditVisibility}
                    className="w-40 text-xs"
                    data-testid="popularity-to"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => saveVis({})}
                  disabled={!canEditVisibility || visSaving || !vis.popularity_from}
                  data-testid="popularity-custom-save"
                  className="rounded-full"
                >
                  <Save className="h-3 w-3 mr-1.5" /> Apply range
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* KPI GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <Stat label="Total bookings" value={stats.bookings.total} hint={`${stats.bookings.past_30d} in last 30d`} icon={Activity} />
        <Stat label="Upcoming" value={stats.bookings.upcoming} hint="not cancelled" icon={Clock} tone="accent" />
        <Stat label="Last 7 days" value={stats.bookings.past_7d} icon={TrendingUp} />
        <Stat label="Cancellation" value={fmtPct(stats.bookings.cancellation_rate_pct)} hint={`${stats.bookings.cancelled} cancelled`} />
        <Stat label="Unique guests" value={stats.guests.unique_total} icon={Users} />
        <Stat label="Repeat guests" value={stats.guests.repeat_count} hint={`${fmtPct(stats.guests.repeat_pct)} of guests`} tone="accent" />
        <Stat label="Total hours delivered" value={fmtHrs(stats.treatments.total_hours)} icon={Sparkles} />
        <Stat label="Rated sessions" value={stats.ratings.count} hint={`avg ${stats.ratings.avg}★`} icon={Star} tone="accent" />
        <Stat label="Ratings revealed early" value={stats.ratings.revealed_early} hint={`of ${stats.ratings.count}`} />
        <Stat label="Charters total" value={stats.charters.total} hint={`${stats.charters.completed} completed`} />
        <Stat label="Active/upcoming charters" value={stats.charters.active_or_upcoming} />
        <Stat label="Report generated" value={new Date(stats.generated_at).toLocaleTimeString()} hint="Auto refresh 30 s" />
      </div>

      {/* Peak hours */}
      <section className="border border-border bg-card">
        <div className="p-4 border-b border-border">
          <div className="overline text-primary/50 text-[10px]">Peak hours (24h histogram)</div>
          <h3 className="font-display text-lg mt-1">When guests book</h3>
        </div>
        <div className="p-4">
          <Sparkline data={peakHours} />
          <div className="mt-2 flex justify-between text-[10px] font-mono text-muted-foreground">
            {[0, 6, 12, 18, 23].map((h) => <span key={h}>{h.toString().padStart(2,'0')}:00</span>)}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top treatments + ratings */}
        <section className="border border-border bg-card">
          <div className="p-4 border-b border-border">
            <div className="overline text-primary/50 text-[10px]">Treatments</div>
            <h3 className="font-display text-lg mt-1">Most-booked (with ratings)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left"><th className="p-2">Treatment</th><th className="p-2 text-right">Bookings</th><th className="p-2 text-right">Hours</th><th className="p-2 text-right">Rating</th></tr>
              </thead>
              <tbody data-testid="top-treatments">
                {treatRatingsList.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">—</td></tr>}
                {treatRatingsList.map((t) => (
                  <tr key={t.treatment_id} className="border-t border-border">
                    <td className="p-2">{t.name}</td>
                    <td className="p-2 text-right font-mono">{t.count}</td>
                    <td className="p-2 text-right font-mono">{Math.round((t.minutes || 0) / 60)}h</td>
                    <td className="p-2 text-right">
                      {t.rating ? (
                        <span className="inline-flex items-center gap-1 text-accent"><Star className="h-3 w-3 fill-accent" /> {t.rating.avg} <span className="text-muted-foreground text-[10px]">({t.rating.count})</span></span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Crew workload */}
        <section className="border border-border bg-card">
          <div className="p-4 border-b border-border">
            <div className="overline text-primary/50 text-[10px]">Crew</div>
            <h3 className="font-display text-lg mt-1">Workload by therapist</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left"><th className="p-2">Therapist</th><th className="p-2 text-right">Sessions</th><th className="p-2 text-right">Hours</th><th className="p-2 text-right">Rating</th></tr>
              </thead>
              <tbody data-testid="crew-workload">
                {workload.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">—</td></tr>}
                {workload.map((c) => {
                  const r = ratings?.by_crew?.[c.crew_id] || null;
                  return (
                    <tr key={c.crew_id} className="border-t border-border">
                      <td className="p-2">{c.name}</td>
                      <td className="p-2 text-right font-mono">{c.count}</td>
                      <td className="p-2 text-right font-mono">{c.hours}h</td>
                      <td className="p-2 text-right">
                        {r ? (
                          <span className="inline-flex items-center gap-1 text-accent"><Star className="h-3 w-3 fill-accent" /> {r.avg} <span className="text-muted-foreground text-[10px]">({r.count})</span></span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Star distribution */}
        <section className="border border-border bg-card">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Star className="h-4 w-4 text-accent" />
            <div>
              <div className="overline text-primary/50 text-[10px]">Ratings</div>
              <h3 className="font-display text-lg mt-1">Star distribution</h3>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-display text-3xl">{stats.ratings.avg}</span>
              <span className="text-muted-foreground text-xs">/ 5 · {stats.ratings.count} votes</span>
            </div>
            <StarBar dist={stats.ratings.dist} />
          </div>
        </section>

        {/* Repeat guests */}
        <section className="border border-border bg-card">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Award className="h-4 w-4 text-accent" />
            <div>
              <div className="overline text-primary/50 text-[10px]">Repeat guests</div>
              <h3 className="font-display text-lg mt-1">Top {(stats.guests.top_guests || []).length} by session count</h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40"><tr className="text-left"><th className="p-2">Guest ID</th><th className="p-2 text-right">Sessions</th></tr></thead>
              <tbody data-testid="top-guests">
                {(stats.guests.top_guests || []).map(([gid, c]) => (
                  <tr key={gid} className="border-t border-border"><td className="p-2 font-mono">{gid.slice(0, 12)}…</td><td className="p-2 text-right font-mono">{c}</td></tr>
                ))}
                {(stats.guests.top_guests || []).length === 0 && <tr><td colSpan={2} className="p-4 text-center text-muted-foreground">—</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Charter-specific report — Manager+ picks a charter and gets a deep report */}
      {canEditVisibility && (
        <section className="border border-border bg-card" data-testid="charter-report-section">
          <div className="p-4 border-b border-border flex flex-wrap items-start gap-3 justify-between">
            <div>
              <div className="overline text-accent flex items-center gap-1"><FileText className="h-3 w-3" /> Charter report</div>
              <h3 className="font-display text-lg mt-1">Deep-dive per trip</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                Pick any charter — active, upcoming or completed — to see the metrics that shape the next charter's spa plan:
                bookings, revenue, hours delivered, top treatments, crew workload, peak hours, per-guest engagement and ratings with the guest's own words.
              </p>
            </div>
            <div className="min-w-[280px]">
              <label className="overline text-primary/60 text-[10px] block mb-1">Select charter</label>
              <Select value={selectedCharter} onValueChange={setSelectedCharter}>
                <SelectTrigger className="w-full" data-testid="charter-report-select"><SelectValue placeholder="Choose a charter…" /></SelectTrigger>
                <SelectContent>
                  {charters.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`charter-opt-${c.id}`}>
                      {c.name} · {c.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {reportLoading && <div className="p-8 text-sm text-muted-foreground">Compiling report…</div>}
          {!reportLoading && !report && <div className="p-8 text-sm text-muted-foreground">Pick a charter above to generate a report.</div>}
          {!reportLoading && report && (
            <div className="p-4 space-y-6" data-testid="charter-report-body">
              {/* Header block */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <Stat label="Charter" value={report.charter.name} />
                <Stat label="Status" value={report.charter.status} tone={report.charter.status === "completed" ? "default" : "accent"} />
                <Stat label="Guests" value={report.charter.guest_count} icon={Users} />
                <Stat label="Bookings" value={report.bookings.completed_or_active} hint={`${report.bookings.cancelled} cancelled`} icon={Activity} />
                <Stat label="Hours delivered" value={fmtHrs(report.bookings.hours_delivered)} icon={Clock} tone="accent" />
                <Stat label="Revenue" value={`€${(report.bookings.revenue || 0).toFixed(0)}`} hint="cancelled excluded" tone="accent" />
              </div>

              {/* Dates + cancellation */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-muted-foreground">
                <div><span className="overline block text-[9px]">Arrival</span>{(report.charter.arrival || "").slice(0, 10) || "—"}</div>
                <div><span className="overline block text-[9px]">Departure</span>{(report.charter.departure || "").slice(0, 10) || "—"}</div>
                <div><span className="overline block text-[9px]">Cancellation rate</span>{fmtPct(report.bookings.cancellation_rate_pct)}</div>
                <div><span className="overline block text-[9px]">Guests without a booking</span>{report.guests.no_booking_yet}</div>
              </div>

              {/* Peak hours + daily trend */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="overline text-primary/50 text-[10px] mb-2">Peak hours</div>
                  <Sparkline data={report.peak_hours || Array(24).fill(0)} />
                  <div className="mt-1 flex justify-between text-[10px] font-mono text-muted-foreground">
                    {[0, 6, 12, 18, 23].map((h) => <span key={h}>{h.toString().padStart(2,'0')}:00</span>)}
                  </div>
                </div>
                <div>
                  <div className="overline text-primary/50 text-[10px] mb-2">Daily activity</div>
                  <div className="flex items-end gap-1 h-16">
                    {(report.daily_activity || []).map((d) => {
                      const max = Math.max(1, ...(report.daily_activity || []).map((x) => x.count));
                      return (
                        <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day} — ${d.count}`}>
                          <div className="w-full bg-primary/40 hover:bg-accent transition-colors" style={{ height: `${(d.count * 100) / max}%` }} />
                        </div>
                      );
                    })}
                    {(report.daily_activity || []).length === 0 && <span className="text-xs text-muted-foreground italic">No bookings yet</span>}
                  </div>
                </div>
              </div>

              {/* Top guests + treatments + crew */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="border border-border">
                  <div className="p-3 border-b border-border overline text-primary/50 text-[10px]">Top guests</div>
                  <table className="w-full text-xs">
                    <tbody data-testid="report-top-guests">
                      {report.guests.top.map((g) => (
                        <tr key={g.guest_id} className="border-t border-border">
                          <td className="p-2">{g.name} <span className="text-[10px] text-muted-foreground">· {g.cabin_name}</span></td>
                          <td className="p-2 text-right font-mono">{g.count}× · {Math.round(g.minutes / 60)}h</td>
                        </tr>
                      ))}
                      {report.guests.top.length === 0 && <tr><td className="p-4 text-center text-muted-foreground">—</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="border border-border">
                  <div className="p-3 border-b border-border overline text-primary/50 text-[10px]">Top treatments</div>
                  <table className="w-full text-xs">
                    <tbody data-testid="report-top-treatments">
                      {report.treatments.top.map((t) => (
                        <tr key={t.treatment_id} className="border-t border-border">
                          <td className="p-2">{t.name}</td>
                          <td className="p-2 text-right font-mono">{t.count}× · €{(t.revenue || 0).toFixed(0)}</td>
                        </tr>
                      ))}
                      {report.treatments.top.length === 0 && <tr><td className="p-4 text-center text-muted-foreground">—</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="border border-border">
                  <div className="p-3 border-b border-border overline text-primary/50 text-[10px]">Crew workload</div>
                  <table className="w-full text-xs">
                    <tbody data-testid="report-crew-workload">
                      {report.crew.workload.map((c) => (
                        <tr key={c.crew_id} className="border-t border-border">
                          <td className="p-2">{c.name}</td>
                          <td className="p-2 text-right font-mono">{c.count}× · {c.hours}h</td>
                        </tr>
                      ))}
                      {report.crew.workload.length === 0 && <tr><td className="p-4 text-center text-muted-foreground">—</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Ratings summary + comments */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-border">
                  <div className="p-3 border-b border-border flex items-baseline justify-between">
                    <div className="overline text-primary/50 text-[10px] flex items-center gap-1"><Star className="h-3 w-3" /> Ratings</div>
                    <div className="text-sm">
                      <span className="font-display text-2xl">{report.ratings.avg}</span>
                      <span className="text-muted-foreground text-[11px] ml-1">/ 5 · {report.ratings.count}</span>
                    </div>
                  </div>
                  <div className="p-3"><StarBar dist={report.ratings.dist} /></div>
                </div>
                <div className="border border-border">
                  <div className="p-3 border-b border-border overline text-primary/50 text-[10px] flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" /> Recent comments
                  </div>
                  <div className="max-h-64 overflow-y-auto" data-testid="report-comments">
                    {(() => {
                      // Memoise the notes-only comment slice — the review flagged
                      // filter-then-map running twice per render as a perf smell.
                      const notedComments = (report.ratings.recent || []).filter((r) => (r.note || "").trim());
                      if (notedComments.length === 0) {
                        return <div className="p-4 text-center text-muted-foreground text-xs">No comments yet.</div>;
                      }
                      return notedComments.map((r) => (
                      <div key={r.id} className="p-3 border-t border-border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="inline-flex items-center gap-1 text-accent text-xs"><Star className="h-3 w-3 fill-accent" strokeWidth={0} />{r.stars}</span>
                          <span className="text-[10px] text-muted-foreground">{r.treatment_name}</span>
                        </div>
                        <div className="text-xs text-foreground/85 italic">"{r.note}"</div>
                        <div className="text-[10px] text-muted-foreground mt-1">— {r.guest_name}</div>
                      </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Developer-only detailed ratings feed */}
      {isDev && ratingsDetailed && (        <section className="border border-destructive/40 bg-destructive/5" data-testid="ratings-detailed-dev">
          <div className="p-4 border-b border-destructive/30 flex items-center justify-between">
            <div>
              <div className="overline text-destructive">Developer only</div>
              <h3 className="font-display text-lg mt-1">Detailed rating feed — with guest names</h3>
              <p className="text-xs text-muted-foreground mt-1">Ratings marked "hidden" are pre-charter-end anonymous for staff; you see them because you're Developer.</p>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left"><th className="p-2">When</th><th className="p-2">Stars</th><th className="p-2">Treatment</th><th className="p-2">Guest</th><th className="p-2">Charter</th><th className="p-2">Note</th><th className="p-2">Visibility</th></tr>
              </thead>
              <tbody>
                {ratingsDetailed.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No ratings yet.</td></tr>}
                {ratingsDetailed.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="p-2 font-mono whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="p-2 text-accent"><Star className="h-3 w-3 inline fill-accent" /> {r.stars}</td>
                    <td className="p-2">{r.treatment_name}</td>
                    <td className="p-2 font-medium">{r.guest_name}</td>
                    <td className="p-2">{r.charter_name || "—"}</td>
                    <td className="p-2 max-w-[200px] truncate" title={r.note}>{r.note || "—"}</td>
                    <td className="p-2 text-[10px] uppercase">
                      {r.reveal_now ? <span className="text-emerald-600">public</span> : <span className="text-muted-foreground">hidden</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
